import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API from "../api";
import "./ProductDetails.css";
import { Helmet } from "react-helmet";
import {
  Share2,
  ShoppingCart,
  ShieldCheck,
  Store,
  MapPin,
  PackageCheck
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ProductDetails() {
  const { vendorProductCode } = useParams();

  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadProduct = async () => {
  try {
    const res = await API.get("/admin/products/all");

    const found = res.data.find(
      (p) => p.vendorProductCode === vendorProductCode
    );

    if (found) {
  setProduct(found);

  if (found.variants?.length > 0) {
    setSelectedVariant(found.variants[0]);
  }
} else {
  setProduct(null);
}

  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
};

    loadProduct();
  }, [vendorProductCode]);

  if (loading) return <p>Loading...</p>;

  if (!product) return <p>Product not found.</p>;
 
  const handleShare = async () => {
  const url = window.location.href;

  try {
    if (navigator.share) {
      await navigator.share({
        title: product.name,
        text: `Check out ${product.name} on Civilink`,
        url,
      });
    } else {
      await navigator.clipboard.writeText(url);
      alert("Product link copied to clipboard.");
    }
  } catch (err) {
    console.error(err);
  }
};

const handleAddToCart = () => {
  if (!localStorage.getItem("token")) {
    window.location.href = "/";
    return;
  }

  const cart = JSON.parse(localStorage.getItem("vendorCart") || "[]");

  const existing = cart.find(
  item =>
    item.vendorProductCode === product.vendorProductCode &&
    item.size === selectedVariant?.size
);

  if (existing) {
    existing.quantity += 1;
  } else {
    cart.push({
      _id: product._id,
      name: product.name,
      vendorProductCode: product.vendorProductCode,
      vendorId: product.vendor?._id,
      productType: product.productType,

      size: selectedVariant?.size,

      price:
  product.productType === "SALE"
    ? selectedVariant?.price ?? product.price
    : undefined,

      dailyPrice:
  product.productType === "RENTAL"
    ? selectedVariant?.dailyPrice
    : undefined,

      quantity: 1,

      days:
        product.productType === "RENTAL"
          ? 1
          : undefined
    });
  }

  localStorage.setItem("vendorCart", JSON.stringify(cart));

  alert("Item added to cart");
};

  return (
    <>
    <Helmet>
      <title>
        {product.name} | {product.vendor?.name} | Civilink
      </title>

      <meta
        name="description"
        content={`Buy ${product.name} from ${product.vendor?.name} in ${
          product.vendor?.location?.city
        }. View price, variants and supplier details on Civilink.`}
      />

      <link
        rel="canonical"
        href={`https://www.civilink.in/product/${product.vendorProductCode}`}
      />

      <meta property="og:type" content="product" />

<meta
  property="og:title"
  content={`${product.name} | ${product.vendor?.name} | Civilink`}
/>

<meta
  property="og:description"
  content={`Buy ${product.name} from ${product.vendor?.name} in ${product.vendor?.location?.city}. View price, variants and supplier details on Civilink.`}
/>

<meta
  property="og:url"
  content={`https://www.civilink.in/product/${product.vendorProductCode}`}
/>

<meta
  property="og:image"
  content={product.imageUrl}
/>
 
<meta
  property="og:image:alt"
  content={product.name}
/>

<meta property="og:site_name" content="Civilink" />

<meta name="twitter:card" content="summary_large_image" />

<meta
  name="twitter:title"
  content={`${product.name} | Civilink`}
/>

<meta
  name="twitter:description"
  content={`Buy ${product.name} from ${product.vendor?.name}.`}
/>

<meta
  name="twitter:image"
  content={product.imageUrl}
/>

<meta
  name="twitter:image:alt"
  content={product.name}
/>

      <script type="application/ld+json">
  {JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Product",

    name: product.name,

    image: product.imageUrl
      ? [product.imageUrl]
      : [],

    description:
  product.description ||
  `Buy ${product.name} from ${product.vendor?.name} on Civilink.`,

    sku: product.vendorProductCode,

    category: product.category,

    brand: {
      "@type": "Brand",
      name: product.brand || product.vendor?.name
    },

    seller: {
      "@type": "Organization",
      name: product.vendor?.name
    },

    offers: {
      "@type": "Offer",

      url: `https://www.civilink.in/product/${product.vendorProductCode}`,

      price:
        product.productType === "SALE"
          ? (
              product.variants?.length
                ? Math.min(...product.variants.map(v => v.price))
                : product.price
            )
          : (
              product.variants?.length
                ? Math.min(...product.variants.map(v => v.dailyPrice))
                : 0
            ),

      priceCurrency: "INR",

      availability: "https://schema.org/InStock"
    }
  })}
</script>

<script type="application/ld+json">
{JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://www.civilink.in"
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Marketplace",
      item: "https://www.civilink.in/marketplace"
    },
    {
      "@type": "ListItem",
      position: 3,
      name: product.name,
      item: `https://www.civilink.in/product/${product.vendorProductCode}`
    }
  ]
})}
</script>
    </Helmet>

    <div className="product-page">

  {/* Top Header */}
  <div className="product-header">

    <button
      className="product-back-btn"
      onClick={() => navigate(-1)}
    >
      ← Back
    </button>

    <button
      className="product-share-btn"
      onClick={handleShare}
    >
      <Share2 size={18} />
      Share
    </button>

  </div>

  {/* Main Card */}
  <div className="product-card">

    <img
      src={product.imageUrl}
      alt={`${product.name} supplied by ${product.vendor?.name} in ${product.vendor?.location?.city}`}
      className="product-image"
    />

    <div className="product-info">

      <h1 className="product-title">
        {product.name}
      </h1>

      <div className="product-price">
        ₹
{product.productType === "SALE"
  ? (
      selectedVariant?.price ?? product.price
    )
  : (
      selectedVariant?.dailyPrice ?? 0
    )}


        {product.productType === "RENTAL" && " / Day"}
      </div>

      <div className="product-badge">
        <ShieldCheck size={16} />
         Civilink Verified
      </div>

      <div className="product-vendor">
        <Store size={16} />
         {product.vendor?.name}
      </div>

      <div className="product-location">
        <MapPin size={16} />
         {product.vendor?.location?.city}
      </div>

      {product.variants?.length > 0 && (
        <>
          <h3 className="variant-title">
            Available Variants
          </h3>

          <div className="variant-list">

            {product.variants.map((v, i) => (

              <div
  key={i}
  className={`variant-btn ${
    selectedVariant?.size === v.size ? "active" : ""
  }`}
  onClick={() => setSelectedVariant(v)}
>
                {v.size}

                <br />

                ₹
                {product.productType === "SALE"
                  ? v.price
                  : `${v.dailyPrice}/day`}
              </div>

            ))}

          </div>
        </>
      )}

      <div className="product-stock">
        <PackageCheck size={16} />
        In Stock
      </div>

      {product.description && (

        <div className="description-card">

          <h3>Description</h3>

          <p>
            {product.description}
          </p>

        </div>

      )}

      <div className="product-actions">

        <button
          className="cart-btn"
          onClick={handleAddToCart}
        >
          <ShoppingCart size={18} />
          Add to Cart
        </button>

        <button
          className="buy-more-btn"
          onClick={() =>
            navigate(`/vendor?category=${product.category}`)
          }
        >
          Buy More
        </button>

      </div>

    </div>

  </div>



    </div>
      </>
  );
}